import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FamilyView from '../components/screens/FamilyView';
import useFamily from '../hooks/useFamily';
import {
  buildRecurrencePayload,
  createRecurrenceForm,
} from '../lib/recurrence';

const initialAcceptInviteForm = { token: '' };
const initialFamilyTaskForm = {
  title: '',
  description: '',
  assignedTo: '',
  priority: 'medium',
  dueDate: '',
  status: 'open',
  recurrence: createRecurrenceForm(),
};
const familyTabValues = new Set(['overview', 'members', 'tasks', 'shared_lists', 'invites']);

function FamilyPage({ auth }) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [showJoinPanel, setShowJoinPanel] = useState(false);
  const [acceptInviteForm, setAcceptInviteForm] = useState(initialAcceptInviteForm);
  const [familyTaskForm, setFamilyTaskForm] = useState(initialFamilyTaskForm);
  const [familyActiveTab, setFamilyActiveTab] = useState('overview');
  const [familyInviteFilter, setFamilyInviteFilter] = useState('pending');
  const focusMemberUserId = searchParams.get('focusMemberUserId') || '';
  const focusInviteId = searchParams.get('focusInviteId') || '';

  const {
    family,
    membership,
    familyRequestId,
    familyMembers,
    familyInvites,
    incomingFamilyInvites,
    familyTasks,
    familySharedLists,
    familyStatus,
    createFamilyStatus,
    inviteStatus,
    familyTasksStatus,
    createFamilyForm,
    createInviteForm,
    familyTaskFilter,
    acceptingIncomingInviteId,
    revokingInviteId,
    lastCreatedInviteToken,
    setCreateFamilyForm,
    setCreateInviteForm,
    setFamilyTaskFilter,
    handleCreateFamily,
    handleCreateInvite,
    handleAcceptInvite,
    handleAcceptIncomingInvite,
    handleRevokeInvite,
    handleCreateFamilyTask,
    handleUpdateFamilyTask,
    handleCompleteFamilyTask,
  } = useFamily(auth.currentUser);

  const pendingInviteCount = useMemo(
    () => familyInvites.filter((invite) => invite?.status === 'pending').length,
    [familyInvites]
  );

  useEffect(() => {
    const inviteToken = searchParams.get('inviteToken');

    if (!inviteToken) {
      return;
    }

    setAcceptInviteForm((current) => ({
      ...current,
      token: inviteToken,
    }));
    setShowJoinPanel(true);
  }, [searchParams]);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (requestedTab && familyTabValues.has(requestedTab)) {
      setFamilyActiveTab(requestedTab);
      return;
    }

    if (focusInviteId || searchParams.get('view')) {
      setFamilyActiveTab('invites');
      return;
    }

    if (focusMemberUserId) {
      setFamilyActiveTab('members');
    }
  }, [focusInviteId, focusMemberUserId, searchParams]);

  const handleCreateFamilySubmit = async (event) => {
    await handleCreateFamily(event, createFamilyForm.name);
  };

  const handleAcceptInviteSubmit = async (event) => {
    event.preventDefault();
    await handleAcceptInvite(acceptInviteForm.token);
  };

  const handleCreateFamilyTaskSubmit = async (event) => {
    event.preventDefault();
    const created = await handleCreateFamilyTask(event, {
      title: familyTaskForm.title,
      description: familyTaskForm.description,
      assignedTo: familyTaskForm.assignedTo,
      priority: familyTaskForm.priority,
      dueDate: familyTaskForm.dueDate,
      status: familyTaskForm.status,
      recurrence: buildRecurrencePayload(familyTaskForm.recurrence),
    });

    if (created) {
      setFamilyTaskForm(initialFamilyTaskForm);
    }
  };

  return (
    <FamilyView
      currentUser={auth.currentUser}
      familyStatus={familyStatus}
      createFamilyStatus={createFamilyStatus}
      inviteStatus={inviteStatus}
      incomingInvitesStatus={inviteStatus}
      acceptInviteStatus={inviteStatus}
      familyTasksStatus={familyTasksStatus}
      familySharedListsStatus={{ type: 'success', message: 'Family shared lists loaded.' }}
      family={family}
      membership={membership}
      familyRequestId={familyRequestId}
      familyMembers={familyMembers}
      familyInvites={familyInvites}
      incomingFamilyInvites={incomingFamilyInvites}
      familyTasks={familyTasks}
      familySharedLists={familySharedLists}
      pendingInviteCount={pendingInviteCount}
      familyActiveTab={familyActiveTab}
      familyInviteFilter={familyInviteFilter}
      familyTaskFilter={familyTaskFilter}
      focusMemberUserId={focusMemberUserId}
      focusInviteId={focusInviteId}
      showJoinPanel={showJoinPanel}
      createFamilyForm={createFamilyForm}
      createInviteForm={createInviteForm}
      acceptInviteForm={acceptInviteForm}
      familyTaskForm={familyTaskForm}
      createFamilyPending={createFamilyStatus.type === 'loading'}
      createInvitePending={inviteStatus.type === 'loading'}
      acceptInvitePending={inviteStatus.type === 'loading'}
      acceptingIncomingInviteId={acceptingIncomingInviteId}
      revokingInviteId={revokingInviteId}
      lastCreatedInviteToken={lastCreatedInviteToken}
      onCreateFamilyFormChange={(field, value) =>
        setCreateFamilyForm((current) => ({ ...current, [field]: value }))
      }
      onCreateInviteFormChange={(field, value) =>
        setCreateInviteForm((current) => ({ ...current, [field]: value }))
      }
      onAcceptInviteFormChange={(field, value) =>
        setAcceptInviteForm((current) => ({ ...current, [field]: value }))
      }
      onFamilyTaskFormChange={(field, value) =>
        setFamilyTaskForm((current) => ({ ...current, [field]: value }))
      }
      onCreateFamily={handleCreateFamilySubmit}
      onCreateInvite={handleCreateInvite}
      onAcceptInvite={handleAcceptInviteSubmit}
      onAcceptIncomingInvite={handleAcceptIncomingInvite}
      onRevokeInvite={handleRevokeInvite}
      onFamilyTabChange={setFamilyActiveTab}
      onFamilyInviteFilterChange={setFamilyInviteFilter}
      onFamilyTaskFilterChange={setFamilyTaskFilter}
      onCreateFamilyTask={handleCreateFamilyTaskSubmit}
      onUpdateFamilyTask={handleUpdateFamilyTask}
      onCompleteFamilyTask={handleCompleteFamilyTask}
      onOpenSharedList={(listId) => navigate(`/lists/family/${listId}`)}
      onOpenSharedListEditor={() =>
        navigate('/lists', {
          state: {
            mode: 'editor',
            scope: 'family',
          },
        })
      }
      onToggleJoinPanel={() => setShowJoinPanel((visible) => !visible)}
    />
  );
}

export default FamilyPage;
